import React from 'react';
import PropTypes from 'prop-types';
import BigCalendar from 'react-big-calendar';
import globalize from 'globalize';
import { Card } from 'react-md';
import { injectIntl, intlShape } from 'react-intl';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import 'globalize/lib/cultures/globalize.culture.en-GB';
import 'globalize/lib/cultures/globalize.culture.fr';
import 'globalize/lib/cultures/globalize.culture.nl';
import 'globalize/lib/cultures/globalize.culture.de';

import { overspeedsSums } from '../../utils/reducers';
import MyToolbar from './MyToolbar';

import './Calendar.css';

BigCalendar.setLocalizer(BigCalendar.globalizeLocalizer(globalize));

const allViews = ['month', 'day'];

const styles = {
  testStyle: {
    display: 'inline-block',
    marginTop: '3px',
  },
};

const Event = injectIntl(({ event, intl }) => (
  <span>
    <strong>{intl.formatTime(event.startDateTime)}</strong>
    {event.plugged && `:  ${intl.formatMessage({ id: 'calendar.event.plugged' })}`}
    {event.unplugged && `:  ${intl.formatMessage({ id: 'calendar.event.unplugged' })}`}
  </span>
));

Event.propTypes = {
  intl: intlShape,
};

const DayBadges = ({
  small = 0, medium = 0, large = 0, xlarge = 0,
}) => (
  <div className="float-right" style={styles.testStyle}>
    <span className="day-badge small" data-content={small}>{small}</span>
    <span className="day-badge medium" data-content={medium}>{medium}</span>
    <span className="day-badge large" data-content={large}>{large}</span>
    <span className="day-badge xlarge" data-content={xlarge}>{xlarge}</span>
  </div>
);

DayBadges.propTypes = {
  small: PropTypes.number,
  medium: PropTypes.number,
  large: PropTypes.number,
  xlarge: PropTypes.number,
};

// eslint-disable-next-line object-curly-newline
const MyDateHeader = ({ events, label, date, onDrillDown }) => {
  const DayOfMonth = () => <div className="float-left md-pointer--hover dayNumber" onClick={onDrillDown}>{label}</div>;
  const dateMidnight = new Date(date).setHours(0, 0, 0, 0);
  const dayEvents = events.filter((e) => {
    const eventDateMidnight = new Date(e.startDateTime).setHours(0, 0, 0, 0);
    return (eventDateMidnight.valueOf() === dateMidnight.valueOf() && !e.plugged && !e.unplugged);
  });
  if (dayEvents.length === 0) return <DayOfMonth />;
  // eslint-disable-next-line object-curly-newline
  const dayOverspeeds = overspeedsSums(dayEvents.map(e => overspeedsSums((e.stats || {}).byOccurences)));
  return (
    [<DayBadges key="dayBadges" {...dayOverspeeds} />,
      <DayOfMonth key="day-of-month" />]
  );
};
MyDateHeader.propTypes = {
  label: PropTypes.node,
  date: PropTypes.instanceOf(Date),
  drilldownView: PropTypes.string,
  onDrillDown: PropTypes.func,
  isOffRange: PropTypes.bool,
};

export const customEventPropGetter = (event) => {
  let overspeedClass = '';
  // eslint-disable-next-line object-curly-newline
  const { small, medium, large, xlarge } = overspeedsSums(((event.stats || {}).byOccurences || {}));
  if (event.plugged) {
    overspeedClass = 'plugged';
  } else if (event.unplugged) {
    overspeedClass = 'unplugged';
  } else if (xlarge) {
    overspeedClass = 'xlarge';
  } else if (large) {
    overspeedClass = 'large';
  } else if (medium) {
    overspeedClass = 'medium';
  } else if (small) {
    overspeedClass = 'small';
  } else {
    overspeedClass = 'no-overspeed';
  }


  return {
    className: overspeedClass,
  };
};




const Calendar = ({ intl, events, handleSelectEvent, view, onView }) => {
  const messages = {
    date: intl.formatMessage({ id: 'calendar.messages.date' }),
    time: intl.formatMessage({ id: 'calendar.messages.time' }),
    event: intl.formatMessage({ id: 'calendar.messages.event' }),
    allDay: intl.formatMessage({ id: 'calendar.messages.allDay' }),
    week: intl.formatMessage({ id: 'calendar.messages.week' }),
    work_week: intl.formatMessage({ id: 'calendar.messages.work_week' }),
    day: intl.formatMessage({ id: 'calendar.messages.day' }),
    month: intl.formatMessage({ id: 'calendar.messages.month' }),
    yesterday: intl.formatMessage({ id: 'calendar.messages.yesterday' }),
    tomorrow: intl.formatMessage({ id: 'calendar.messages.tomorrow' }),
    today: intl.formatMessage({ id: 'calendar.messages.today' }),
    agenda: intl.formatMessage({ id: 'calendar.messages.agenda' }),

    showMore: total => `+${total} ${intl.formatMessage({ id: 'calendar.messages.more' })}`,
  };


  return (
    <Card style={{ height: 'calc(100vh - 140px - 14px)' }}>
      <BigCalendar
        eventPropGetter={customEventPropGetter}
        components={{
          event: Event,
          toolbar: MyToolbar,
          month: {
            dateHeader: props => (
              <MyDateHeader events={events} {...props} />
            ),
          },
        }}
        view={view}
        onView={onView}
        onSelectEvent={handleSelectEvent}
        messages={messages}
        events={events}
        startAccessor={event => new Date(event.startDateTime)}
        endAccessor={event => new Date(event.endDateTime)}
        views={allViews}
        step={30}
        showMultiDayTimes
        defaultDate={new Date()}
        culture={intl.locale}
      />
    </Card>
  );
};

Calendar.propTypes = {
  intl: intlShape.isRequired,
  events: PropTypes.array.isRequired,
  handleSelectEvent: PropTypes.func.isRequired,
};

export default injectIntl(Calendar);



// WEBPACK FOOTER //
// ./src/components/Calendar/index.js